import z from 'zod';

import { useViewInstances } from '#core/hooks/fusion/useViewInstances.ts';
import { parseView } from '#core/lib/cognite/view.ts';
import { InstanceIdType } from '#core/schema/defs.ts';

import { Instructions } from '#plugin-chat/lib/instructions/Instructions.tsx';

interface EntityRef {
  externalId: string;
}

interface FieldGroup {
  field: EntityRef;
  asset: EntityRef;
  reservoirs: EntityRef[];
}

const FlatHierarchyModel = z.object({
  ...InstanceIdType.shape,
  company: InstanceIdType,
  asset: InstanceIdType,
  well: InstanceIdType,
  location: InstanceIdType,
  field: InstanceIdType,
  reservoir: InstanceIdType,
  string: InstanceIdType,
});

type FlatHierarchyRow = z.infer<typeof FlatHierarchyModel>;

const FlatHierarchyAgentBaseView = parseView({
  view: { externalId: 'FlatHierarchy_Agent' },
  model: FlatHierarchyModel,
  dataModel: {
    space: 'dm-ipso-agent-solution-data-model',
    externalId: 'ipso_network_model_agent_data_model',
    instanceSpace: 'dm-ipso-solution-instances',
    version: '1',
  },
});

export function EntityOverviewInstructions() {
  const { data: hierarchyRows } = useViewInstances(FlatHierarchyAgentBaseView, {
    // Reference data: load once, never go stale → never re-suspend (which would blank the chat).
    // The view is already scoped to in-scope assets, so no filter is needed.
    staleTime: Number.POSITIVE_INFINITY,
  });

  // Deterministic order (by externalId) so the rendered block is stable regardless
  // of the order CDF returns instances in, avoiding needless re-renders of <Chat>.
  const byExternalId = (a: EntityRef, b: EntityRef) => a.externalId.localeCompare(b.externalId);
  const isUnknown = (str = '') => str.toLowerCase().includes('unknown');

  type FieldRow = FlatHierarchyRow & { field: EntityRef };
  const rowsByFieldExternalId = Object.groupBy(
    ((hierarchyRows as FlatHierarchyRow[] | undefined) ?? []).filter(
      (row): row is FieldRow => row.field?.externalId != null && !isUnknown(row.field.externalId),
    ),
    (row: FieldRow) => row.field.externalId,
  );

  const fieldGroups: FieldGroup[] = Object.values(rowsByFieldExternalId)
    .filter((rows: FieldRow[] | undefined): rows is FieldRow[] => rows != null && rows.length > 0)
    .map((rows: FieldRow[]) => {
      const reservoirs = Object.values(
        Object.groupBy(
          rows
            .map((row: FieldRow) => row.reservoir)
            .filter(
              (reservoir: EntityRef | null | undefined): reservoir is EntityRef =>
                reservoir?.externalId != null && !isUnknown(reservoir.externalId),
            ),
          (reservoir: EntityRef) => reservoir.externalId,
        ),
      )
        .map((reservoirRows: EntityRef[] | undefined) => reservoirRows?.[0])
        .filter((reservoir: EntityRef | undefined): reservoir is EntityRef => reservoir !== undefined)
        .toSorted(byExternalId);
      return { field: rows[0]?.field ?? { externalId: '' }, asset: rows[0]?.asset ?? { externalId: '' }, reservoirs };
    })
    .toSorted((a: FieldGroup, b: FieldGroup) => byExternalId(a.field, b.field));

  return (
    <Instructions>
      <h1>Entity Overview</h1>
      <p>
        Note: Users may use abbreviations when referencing these entities, as well as when referencing associated
        entities. For example, MENDER wells and strings have a MN prefix, DABBIYA wells/strings have a DY prefix, etc.
      </p>
      <h2>Entity Aliases</h2>
      <dl>
        <dt>NEB (Asset)</dt>
        <dd>North East Bab</dd>
        <dt>Al Nouf (Field)</dt>
        <dd>Dabbiya (DY)</dd>
      </dl>
      <h2>Fields and related reservoirs</h2>
      <p>
        This is the complete, authoritative registry of every in-scope field and its reservoirs, each name quoted
        verbatim exactly as stored. When a user names a field or reservoir, match their wording against this list
        directly — ignoring case, spacing, punctuation and the abbreviations above — and adopt the spelling shown here
        as the canonical name. A token that matches an entry here is resolved: it exists and is in scope, with no lookup
        needed to confirm it. A query can only ever return these same entities, so never read an empty query result as
        evidence that a listed field or reservoir is missing — recheck the spelling against this list instead. If a
        token matches nothing here, it is not an in-scope field or reservoir: say so and ask which listed entity was
        meant, never guess alternative spellings.
      </p>
      <ul>
        {fieldGroups.map(({ field, asset, reservoirs }: FieldGroup) => {
          const reservoirSuffix =
            reservoirs.length > 0
              ? ` with reservoirs: ${reservoirs
                  .map((reservoir: EntityRef) => reservoir.externalId.split(':').slice(1).join(':'))
                  .join(', ')}`
              : '';
          return (
            <li key={field.externalId}>
              {field.externalId.split(':').slice(1).join(':')} is in asset{' '}
              {asset.externalId.split(':').slice(1).join(':')}
              {reservoirSuffix}
            </li>
          );
        })}
      </ul>

      <p>
        <strong>
          MANDATORY RULE: DO NOT run parallel tool calls. This will consistently cause tool calls to fail. Run tool
          calls sequentially ONLY.
        </strong>
      </p>
    </Instructions>
  );
}
